import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/UserAttrContext";
import { setLanguage, t } from "../i18n";
import styles from "./Settings.module.css";
import { FaChevronRight, FaArrowLeft } from "react-icons/fa";
import { DEFAULT_AVATAR, resolveAvatarUrl } from "../utils/avatar";
import { useTheme } from "../hooks/useTheme";

const Settings = () => {
  const navigate = useNavigate();
  const { user, updateAttributes } = useUser();
  const { darkMode, setDarkMode } = useTheme();

  const userToken = sessionStorage.getItem("token");
  const userId = sessionStorage.getItem("user");
  const [languageState, setLanguageState] = useState(sessionStorage.getItem("language") || "en");

  const [activePopup, setActivePopup] = useState(null);
  const [alertMessage, setAlertMessage] = useState("");
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [blockedPosts, setBlockedPosts] = useState([]);
  const [allPosts, setAllPosts] = useState([]);

  const [picture, setPicture] = useState("");

  const [avatarTemp, setAvatarTemp] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [usernameTemp, setUsernameTemp] = useState("");
  const [email, setEmail] = useState("");
  const [emailTemp, setEmailTemp] = useState("");
  const [passwordTemp, setPasswordTemp] = useState("");
  const handlePopupOpen = (popupType) => {
    setAvatarPreview(user.picture);
    setUsernameTemp(user.username);
    setActivePopup(popupType);
  };

  const handlePopupClose = () => {
    setAvatarTemp(null);
    setUsernameTemp(user.username);
    setEmailTemp("");
    setPasswordTemp("");
    setActivePopup(null);
  };

  const handleAvatarUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setAlertMessage(t("upload_image_file"));
        return;
      } else if (file.size > 2 * 1024 * 1024) {
        setAlertMessage(t("file_size_exceeds"));
        return;
      } else {
        setAvatarTemp(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setAvatarPreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleAvatarChange = async (event) => {
    event.preventDefault();
    if (!avatarTemp) return;
    const formData = new FormData();
    formData.append("uploaderID", userId);
    formData.append("attributes", JSON.stringify({}));
    formData.append("file", avatarTemp);
    try {
      const res = await fetch(process.env.REACT_APP_API_PATH + "/file-uploads", {
        method: "POST",
        headers: {
          Authorization: "Bearer " + userToken,
        },
        body: formData,
      });
      const result = await res.json();
      const pictureUrl = resolveAvatarUrl(result.path);
      updateAttributes({ picture: pictureUrl });
      setPicture(pictureUrl);
      setAlertMessage(t("avatar_uploaded_successfully"));
    } catch (err) {
      console.error(err);
      setAlertMessage(t("failed_to_upload_avatar"));
    }
    handlePopupClose();
  };

  const handleUsernameChange = () => {
    if (usernameTemp.trim().length < 3) {
      setAlertMessage(t("username_min_length"));
      return;
    }
    if (!/^[a-zA-Z0-9._]+$/.test(usernameTemp)) {
      setAlertMessage(t("username_invalid_characters"));
      return;
    }
    updateAttributes({ username: usernameTemp });
    setAlertMessage(t("username_updated_successfully"));
    handlePopupClose();
  };

  const handleEmailChange = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTemp)) {
      setAlertMessage(t("enter_valid_email"));
      return;
    }
    try {
      const res = await fetch(process.env.REACT_APP_API_PATH + "/users/" + userId, {
        method: "PATCH",
        headers: {
          Authorization: "Bearer " + userToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: emailTemp }),
      });
      const result = await res.json();
      console.log(result);
      setEmail(emailTemp);
      setAlertMessage(t("email_updated_successfully"));
    } catch (err) {
      console.log(err);
    }
    handlePopupClose();
  };

  const handlePasswordChange = async () => {
    if (passwordTemp.length < 8) {
      setAlertMessage(t("password_min_length"));
      return;
    }
    try {
      const res = await fetch(process.env.REACT_APP_API_PATH + "/users/" + userId, {
        method: "PATCH",
        headers: {
          Authorization: "Bearer " + userToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password: passwordTemp }),
      });
      const result = await res.json();
      console.log(result);
      setAlertMessage(t("password_updated_successfully"));
    } catch (err) {
      console.log(err);
    }
    handlePopupClose();
  };

  const handleDarkModeToggle = () => {
    const nextDarkMode = !darkMode;
    updateAttributes({ darkMode: nextDarkMode });
    setDarkMode(nextDarkMode);
  };

  const handleLanguageChange = (language) => {
    setLanguage(language);
    setLanguageState(language);
    updateAttributes({ language });
  };

  const handleDeleteAccount = async () => {
    try {
      const loginRes = await fetch(`${process.env.REACT_APP_API_PATH}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
          password: passwordTemp,
        }),
      });

      if (loginRes.status !== 200) {
        setAlertMessage(t("incorrect_password"));
        return;
      }

      const deleteRes = await fetch(`${process.env.REACT_APP_API_PATH}/users/${userId}?relatedObjectsAction=delete`, {
        method: "DELETE",
        headers: {
          Authorization: "Bearer " + userToken,
          "Content-Type": "application/json",
        },
      });

      if (deleteRes.status === 204) {
        setAlertMessage(t("account_deleted_successfully"));
        sessionStorage.clear();
        navigate("/");
      } else if (deleteRes.status === 401) {
        setAlertMessage(t("unauthorized_request"));
      } else {
        setAlertMessage(t("failed_to_delete_account"));
      }
    } catch (err) {
      console.error(err);
      setAlertMessage(t("error_occurred"));
    }
  };

  const fetchBlockedPosts = useCallback(() => {
    fetch(`${process.env.REACT_APP_API_PATH}/users/${userId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
    })
      .then((res) => res.json())
      .then((result) => {
        const blocked = result.attributes?.blockedPosts || [];
        setBlockedPosts(blocked);
      })
      .catch((error) => console.error(t("error_fetching_blocked_posts"), error));
  }, [userId, userToken]);

  const fetchAllPosts = useCallback(() => {
    fetch(`${process.env.REACT_APP_API_PATH}/posts?limit=9999`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const posts = data && data[0] ? data[0] : [];
        setAllPosts(posts);
      })
      .catch((err) => console.error(t("error_fetching_posts"), err));
  }, [userToken]);

  const unblockPost = (postId) => {
    fetch(`${process.env.REACT_APP_API_PATH}/users/${userId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
    })
      .then((res) => res.json())
      .then((userData) => {
        const currentBlockedPosts = userData.attributes?.blockedPosts || [];
        const updatedBlockedPosts = currentBlockedPosts.filter((id) => id !== postId);
        return fetch(`${process.env.REACT_APP_API_PATH}/users/${userId}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
          body: JSON.stringify({
            attributes: {
              ...userData.attributes,
              blockedPosts: updatedBlockedPosts,
            },
          }),
        });
      })
      .then(() => {
        setBlockedPosts(blockedPosts.filter((id) => id !== postId));
        setAlertMessage(t("Post unblocked successfully"));
        fetchAllPosts();
      })
      .catch((error) => {
        console.error(t("Error unblocking post"), error);
        setAlertMessage(t("Failed to unblock post"));
      });
  };

const [hasFetchedBlockedPosts, setHasFetchedBlockedPosts] = useState(false);
useEffect(() => {
  if (userToken) {
    fetch(process.env.REACT_APP_API_PATH + "/users/" + userId, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + userToken,
      },
    })
      .then((res) => res.json())
      .then((result) => {
        if (result) {
          if (result.email) setEmail(result.email);
          setPicture(result.attributes?.picture || DEFAULT_AVATAR);
          if (typeof result.attributes?.darkMode === "boolean") {
            setDarkMode(result.attributes.darkMode);
          }
          setLanguageState(result.attributes?.language || "en");
          setLanguage(result.attributes?.language || "en");
        }
      })
      .catch((err) => {
        console.log(err);
      });

    if (!hasFetchedBlockedPosts) {
      fetchBlockedPosts();
      fetchAllPosts();
      setHasFetchedBlockedPosts(true);
    }
  }
}, [darkMode, fetchAllPosts, fetchBlockedPosts, hasFetchedBlockedPosts, setDarkMode, userId, userToken]);

  return (
    <div className={styles.settings}>
      <div className={styles.settingsHeader}>
        <FaArrowLeft className={styles.backArrow} onClick={() => navigate("/")} />
        <h2 className={styles.settingsTitle}>{t("settings")}</h2>
      </div>
      <div className={styles.settingsContainer}>
        <h3>{t("general")}</h3>
        {picture && <img src={picture} alt={t("avatar")} className={styles.avatarPreview} />}
        <div className={styles.settingsItem} onClick={() => handlePopupOpen("avatar")} >
          <span>{t("avatar")}</span>
          <FaChevronRight className={styles.arrow} />
        </div>
        <div className={styles.settingsItem} onClick={() => handlePopupOpen("username")}>
          <span>{t("username")}</span>
          <div className={styles.itemContainer}>
            <span>{user.username}</span>
            <FaChevronRight className={styles.arrow} />
          </div>
        </div>
        <div className={styles.settingsItem} onClick={() => handlePopupOpen("email")}>
          <span>{t("email")}</span>
          <div className={styles.itemContainer}>
            <span>{email}</span>
            <FaChevronRight className={styles.arrow} />
          </div>
        </div>
        <div className={styles.settingsItem} onClick={() => handlePopupOpen("password")}>
          <span>{t("password")}</span>
          <FaChevronRight className={styles.arrow} />
        </div>
        <div className={`${styles.settingsItem} ${styles.noHover}`} style={{ cursor: "default" }}>
          <span>{t("dark_mode")}</span>
          <label className={styles.switch}>
            <input
              type="checkbox"
              checked={darkMode}
              onChange={() => handleDarkModeToggle()}
            />
            <span className={styles.slider}></span>
          </label>
        </div>
        <div className={styles.settingsItem} onClick={() => handlePopupOpen("language")}>
          <span>{t("language")}</span>
          <div className={styles.itemContainer}>
            <span>{languageState === "en" ? "English" : "中文"}</span>
            <FaChevronRight className={styles.arrow} />
          </div>
        </div>
        <div className={styles.settingsItem} onClick={() => setShowUnblockModal(true)}>
          <span>{t("unblock_posts")}</span>
          <FaChevronRight className={styles.arrow} />
        </div>
        <h3>{t("advanced")}</h3>
        <div className={styles.settingsItem} onClick={() => handlePopupOpen("delete")}>
          <span>{t("delete_account")}</span>
          <FaChevronRight className={styles.arrow} />
        </div>
      </div>
      {alertMessage !== "" && (
        <div className={styles.popup} style={{ zIndex: 1 }}>
          <div className={styles.inputContainer}>
            <p>{alertMessage}</p>
            <button className={styles.button} onClick={() => setAlertMessage("")}>{t("back")}</button>
          </div>
        </div>
      )}
      {activePopup === "avatar" && (
        <div className={styles.popup}>
          <div className={styles.inputContainer}>
            <p>{t("change_avatar")}</p>
            {avatarPreview && <img src={avatarPreview} alt={t("avatar")} className={styles.avatarPreview} />}
            <input type="file" accept="image/*" onChange={handleAvatarUpload} />
            <div className={styles.buttonContainer}>
              <button className={styles.button} onClick={handlePopupClose}>{t("back")}</button>
              <button className={styles.button} onClick={handleAvatarChange} disabled={!avatarTemp}>{t("change")}</button>
            </div>
          </div>
        </div>
      )}
      {activePopup === "username" && (
        <div className={styles.popup}>
          <div className={styles.inputContainer}>
            <p>{t("enter_new_username")}</p>
            <input type="text" value={usernameTemp} onChange={(e) => setUsernameTemp(e.target.value)} />
            <div className={styles.buttonContainer}>
              <button className={styles.button} onClick={handlePopupClose}>{t("back")}</button>
              <button className={styles.button} onClick={handleUsernameChange}>{t("change")}</button>
            </div>
          </div>
        </div>
      )}
      {activePopup === "email" && (
        <div className={styles.popup}>
          <div className={styles.inputContainer}>
            <p>{t("enter_new_email")}</p>
            <input type="email" value={emailTemp} onChange={(e) => setEmailTemp(e.target.value)} />
            <div className={styles.buttonContainer}>
              <button className={styles.button} onClick={handlePopupClose}>{t("back")}</button>
              <button className={styles.button} onClick={handleEmailChange}>{t("change")}</button>
            </div>
          </div>
        </div>
      )}
      {activePopup === "password" && (
        <div className={styles.popup}>
          <div className={styles.inputContainer}>
            <p>{t("enter_new_password")}</p>
            <input type="password" value={passwordTemp} onChange={(e) => setPasswordTemp(e.target.value)} />
            <div className={styles.buttonContainer}>
              <button className={styles.button} onClick={handlePopupClose}>{t("back")}</button>
              <button className={styles.button} onClick={handlePasswordChange}>{t("change")}</button>
            </div>
          </div>
        </div>
      )}
      {activePopup === "language" && (
        <div className={styles.popup}>
          <div className={styles.inputContainer}>
            <p>{t("select_language")}</p>
            <div className={styles.buttonContainer}>
              <button
                className={styles.button}
                onClick={() => handleLanguageChange("en")}
                disabled={languageState === "en"}
              >
                English
              </button>
              <button
                className={styles.button}
                onClick={() => handleLanguageChange("zh")}
                disabled={languageState === "zh"}
              >
                中文
              </button>
            </div>
            <button className={styles.button} onClick={handlePopupClose}>
              {t("back")}
            </button>
          </div>
        </div>
      )}
      {activePopup === "delete" && (
        <div className={styles.popup}>
          <div className={styles.inputContainer}>
            <p>{t("enter_password_to_delete")}</p>
            <input type="password" value={passwordTemp} onChange={(e) => setPasswordTemp(e.target.value)} />
            <div className={styles.buttonContainer}>
              <button className={styles.button} onClick={handlePopupClose}>{t("back")}</button>
              <button className={styles.deleteButton} onClick={handleDeleteAccount} disabled={!passwordTemp}>{t("delete")}</button>
            </div>
          </div>
        </div>
      )}
     {showUnblockModal && (
  <div className={`${styles.popup} ${styles.unblockPopup}`}>
    <div className={styles.inputContainer}>
      <p>{t("unblock_posts")}</p>
      {blockedPosts.length === 0 ? (
        <p style={{ marginBottom: "10px" }}>{t("no_blocked_posts")}</p>
      ) : (
        <div className={styles.unblockPostsList}>
          {allPosts
            .filter((post) => blockedPosts.includes(post.id))
            .map((post) => (
              <div key={post.id} className={styles.unblockPostItem}>
              <div className={styles.unblockPostText}>
                <span className={styles.unblockUsername}>
                  {post.author?.attributes?.username || t("unknown_user")}
                </span>
                <span className={styles.unblockTitle}>
                  {post.attributes?.title || t("no_blocked_posts")}
                </span>
              </div>
              <button className={styles.unblockPostButton} onClick={() => unblockPost(post.id)}>
                {t("unblock")}
              </button>
            </div>            
            ))}
        </div>
      )}
      <button className={styles.button} onClick={() => setShowUnblockModal(false)}>
        {t("back")}
      </button>
    </div>
  </div>
)}
    </div>
  );
};

export default Settings;
